import React, { useCallback, useEffect, useMemo } from 'react';
import Modal, { ModalBody } from '@strava/ui/Modal';
import {
  useStravaAnalytics,
  useStravaErrorLogging,
  useStravaTranslations
} from '@strava/container-context';
import { useApple } from '@strava/react-hooks/src/use-apple';
import { AlertContainer, AlertProvider } from '@strava/ui/Alert';
import Button, { LinkButton } from '@strava/ui/Button';
import VisuallyHidden from '@strava/ui/VisuallyHidden';
import ActionsCancelNormalSmall from '@strava/icons/ActionsCancelNormalSmall';
import { color } from '@strava/ui-tokens/js';
import { Markup } from 'interweave';
import clsx from '@strava/ui/clsx';
import AccountAuthButton from '../AccountAuthButton';
import OrDivider from '../OrDivider';
import EmailRegistrationForm, {
  EmailRegistrationFormProps,
  EMAIL_REGISTRATION_FORM_ALERT_ID,
  AnalyticsFieldsTypes
} from '../EmailRegistrationForm';
import DisclaimerText from '../DisclaimerText';
import { APPLE, FACEBOOK, GOOGLE, HIDE_TIME_DELAY_MODAL } from '../constants';
import { useSignUpModalContext } from './SignUpModalContext';
import styles from './SignUpModal.module.scss';
import {
  devLog,
  isSessionStorageAvailable,
  isValidStravaUrlForCurrentHost
} from '@strava/utils';
import Avatar from '@strava/ui/Avatar';
import { AuthenticationData } from '../../authentication.types';

// using `require` here instead of import to avoid adding `declare module '*.svg'`
// everywhere the `@strava/authentication-ui` package is imported
// eslint-disable-next-line @typescript-eslint/no-var-requires
const echelon = require('./assets/strava-echelon.svg');

export const SESSION_STORAGE_REDIRECT_KEY = 'redirect_to_after_signup';

export type SignUpModalProps = {
  category: string;
  className?: string;
} & AuthenticationData &
  EmailRegistrationFormProps;

const i18nPrefix = 'sign_up_modal';
const AUTH_BUTTONS = [GOOGLE, APPLE, FACEBOOK] as const;

const SignUpModal = ({
  className,
  authorizationUrls,
  appleState,
  category,
  ...props
}: SignUpModalProps) => {
  const { track: _track } = useStravaAnalytics();
  const { t } = useStravaTranslations();
  const { logError } = useStravaErrorLogging();
  const { signin: appleSignin } = useApple(appleState, logError);

  const {
    isSignUpModalOpen,
    dismissSignUpModal,
    variant,
    redirectUrl,
    customHeader
  } = useSignUpModalContext();

  const track = useCallback(
    (fields: AnalyticsFieldsTypes) => {
      _track({
        category,
        page: 'signup_modal',
        ...fields,
        properties: {
          copy_variant: variant,
          ...fields.properties
        }
      });
    },
    [_track, category, variant]
  );

  const redirectUrlIsValid = useCallback((redirectUrl: string | null) => {
    if (!redirectUrl) return false;

    return isValidStravaUrlForCurrentHost(
      redirectUrl,
      new URL(window?.location?.href).host
    );
  }, []);

  const persistRedirectUrlToMemory = (redirectUrl?: string) => {
    if (redirectUrl && isSessionStorageAvailable()) {
      if (redirectUrlIsValid(redirectUrl)) {
        sessionStorage.setItem(SESSION_STORAGE_REDIRECT_KEY, redirectUrl);
        devLog(`[SUM] ::: setting sessionStorage with ${redirectUrl}`);
      } else {
        devLog(`[SUM] ::: ${redirectUrl} is an invalid redirect url`);
      }
    }
  };

  const handleExtAuthBtnClick = (
    option: typeof FACEBOOK | typeof GOOGLE | typeof APPLE
  ) => {
    track({ action: 'click', element: `${option}_signup` });

    // on auth click, we save the return/redirect url in session storage
    persistRedirectUrlToMemory(redirectUrl);

    try {
      if (option === APPLE) {
        appleSignin();
      } else {
        window.location.assign(authorizationUrls[option]);
      }
    } catch (e) {
      logError(e);
      sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_KEY);
    }
  };

  const onDismissSignUpModal = useCallback(() => {
    dismissSignUpModal();
  }, [dismissSignUpModal]);

  useEffect(() => {
    if (isSignUpModalOpen) {
      // register screen_enter event each time modal is opened
      track({ action: 'screen_enter' });

      if (sessionStorage.getItem(HIDE_TIME_DELAY_MODAL) !== 'true') {
        // repress time delayed modal since modal has now been seen
        sessionStorage.setItem(HIDE_TIME_DELAY_MODAL, 'true');
      }
    }
  }, [isSignUpModalOpen, track]);

  const loginLink = useMemo(() => {
    if (redirectUrl) {
      return `/login?redirect=${encodeURIComponent(redirectUrl)}`;
    }
    return '/login';
  }, [redirectUrl]);

  return (
    <AlertProvider>
      <Modal
        isOpen={isSignUpModalOpen}
        onDismiss={onDismissSignUpModal}
        aria-labelledby="sign-up-modal-header"
        className={styles.signUpModal}
      >
        <ModalBody className={clsx(styles.modalBody, className)}>
          <div className={styles.headerButtons}>
            <LinkButton
              href={loginLink}
              variant="text"
              className={styles.loginButton}
              onClick={() => track({ action: 'click', element: 'login' })}
            >
              {t(`${i18nPrefix}.log_in`)}
            </LinkButton>
            <Button
              onClick={() => {
                track({ action: 'click', element: 'close_modal' });
                onDismissSignUpModal();
              }}
              type="button"
              variant="icon"
              className={styles.closeButton}
              title={t(`${i18nPrefix}.close`)}
              data-cy="sign-up-modal-close-button"
            >
              <VisuallyHidden>{t(`${i18nPrefix}.close`)}</VisuallyHidden>
              <ActionsCancelNormalSmall fill={color.colorCoreO3} />
            </Button>
          </div>

          <div className={styles.headerText}>
            {variant === 'follow_athlete' && customHeader?.entityImgSrc ? (
              <Avatar src={customHeader?.entityImgSrc} type="athlete" />
            ) : (
              <img src={echelon} alt="" width="30px" height="30px" />
            )}
            <h2 id="sign-up-modal-header">
              {variant && (
                <Markup
                  content={t(`${i18nPrefix}.${variant}.title_html`, {
                    ...(customHeader?.entityName && {
                      name: customHeader.entityName
                    })
                  })}
                  noWrap={true}
                />
              )}
            </h2>
            <p>{t(`${i18nPrefix}.${variant}.subtitle`)}</p>
          </div>

          <AlertContainer containerId={EMAIL_REGISTRATION_FORM_ALERT_ID} />

          <div className={styles.authButtons}>
            {AUTH_BUTTONS.map((option) => (
              <AccountAuthButton
                variant={option}
                onClick={() => handleExtAuthBtnClick(option)}
                data-testid={`${option}_auth_btn`}
                key={option}
              >
                {t(`${i18nPrefix}.signup_ctas.${option}_cta`)}
              </AccountAuthButton>
            ))}
          </div>

          <OrDivider
            text={t(`${i18nPrefix}.or_sign_up_with_email`)}
            theme="light"
            className={styles.orDivider}
          />

          <EmailRegistrationForm
            authorizationUrl={authorizationUrls.email}
            track={track}
            idPrefix="sign-up-modal"
            theme="light"
            alertConfig={{ className: styles.alert, contentPosition: 'center' }}
            onSubmit={() => persistRedirectUrlToMemory(redirectUrl)}
            {...props}
          />

          <DisclaimerText className={styles.disclaimerText} />
        </ModalBody>
      </Modal>
    </AlertProvider>
  );
};

export default SignUpModal;
